'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  CloseButton,
  Dialog,
  Flex,
  Grid,
  GridItem,
  HStack,
  Portal,
  Skeleton,
  Text,
  Wrap,
} from '@chakra-ui/react'
import { Printer } from 'lucide-react'
import { useReactToPrint } from 'react-to-print'
import { AppCard, toaster } from '@/shared'
import type { IApiResponse } from '@/shared/types/api'
import { getAxiosErrorData } from '@/shared/utils/axios-error'
import { usePrintBarcodeMutation } from '@/entities/blood-collection/api'
import type {
  IBloodCollectionBarcode,
  IBloodCollectionBarcodePrintData,
  IBloodCollectionDetail,
} from '@/entities/blood-collection/model'
import { BarcodeLabelPreview } from './BarcodeLabelPreview'
import { formatBirthDate, formatConfigDateTime } from '../lib/label-helpers'
import {
  getLabelGeometry,
  getLabelPageStyle,
  getLabelPreviewScale,
  useLabelPrintSettings,
} from '@/shared/lib/label-size'
import {
  LabelPrintSettings,
  LabelSheet,
} from '@/shared/ui/label-print-settings'

interface Props {
  open: boolean
  collectionStageId: string
  barcodes: IBloodCollectionBarcode[]
  detail?: IBloodCollectionDetail
  onClose: () => void
}

export function BloodCollectionBarcodeModal({
  open,
  collectionStageId,
  barcodes,
  detail,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation()
  const previewRef = useRef<HTMLDivElement>(null)
  const [selectedBarcodeId, setSelectedBarcodeId] = useState<string | null>(
    null,
  )
  // Data returned by the print endpoint, kept per tube so switching tubes
  // doesn't lose an already-confirmed print payload.
  const [printedData, setPrintedData] = useState<
    Record<string, IBloodCollectionBarcodePrintData>
  >({})
  const [pendingPrint, setPendingPrint] = useState(false)

  const { mutate: printBarcode, isPending: isPrinting } =
    usePrintBarcodeMutation(collectionStageId)

  // Default to the first tube whenever the set of barcodes changes.
  useEffect(() => {
    if (open && barcodes.length) {
      setSelectedBarcodeId((prev) =>
        prev && barcodes.some((b) => b.id === prev) ? prev : barcodes[0].id,
      )
    }
  }, [open, barcodes])

  const selectedBarcode = useMemo(
    () => barcodes.find((b) => b.id === selectedBarcodeId) ?? null,
    [barcodes, selectedBarcodeId],
  )

  // Latest collection date from the stage history — the print endpoint is the
  // authoritative source, but the preview must render before it is called.
  const collectionDatetime = useMemo(() => {
    const dates = (detail?.history ?? [])
      .map((item) => item.collection_datetime)
      .filter(Boolean)
    if (!dates.length) return ''
    return dates.reduce((latest, current) =>
      new Date(current).getTime() > new Date(latest).getTime()
        ? current
        : latest,
    )
  }, [detail])

  // Label data assembled from what we already have, so nothing is fetched
  // until the user actually asks for a print.
  const previewData = useMemo<IBloodCollectionBarcodePrintData | null>(() => {
    if (!detail || !selectedBarcode) return null
    return {
      donor_card_number: detail.donor_card_number,
      donor_full_name: detail.donor_full_name,
      birth_date: detail.birth_date,
      gender: detail.gender,
      collection_datetime: collectionDatetime,
      donor_category_name: detail.donor_category_name,
      donation_type_name: detail.donation_type_name,
      tube_color_name: selectedBarcode.tube_color_name,
      preliminary_blood_group: detail.preliminary_blood_group,
      rh_factor: detail.rh_factor,
      hemoglobin_value: detail.hemoglobin_value,
      screening_doctor_fio: detail.screening_doctor_fio,
      nurse_fio: '',
      barcode_value: selectedBarcode.barcode_value,
      analyses: selectedBarcode.analyses,
    }
  }, [detail, selectedBarcode, collectionDatetime])

  const printData =
    (selectedBarcodeId ? printedData[selectedBarcodeId] : null) ?? previewData

  // Roll, paper and position — picked here, remembered on this computer.
  const printSettings = useLabelPrintSettings()
  const { size: labelSize, paper, position } = printSettings
  const geometry = useMemo(() => getLabelGeometry(labelSize), [labelSize])
  const previewScale = getLabelPreviewScale(paper)

  const reactToPrintFn = useReactToPrint({
    contentRef: previewRef,
    // Pins the print sheet to the physical label; without it the label is laid
    // out on default paper and the driver crops it.
    pageStyle: getLabelPageStyle(paper),
  })

  // Open the browser print dialog only once the confirmed payload has been
  // committed to the DOM.
  useEffect(() => {
    if (!pendingPrint) return
    setPendingPrint(false)
    reactToPrintFn()
  }, [pendingPrint, reactToPrintFn])

  const handlePrint = () => {
    if (!selectedBarcodeId) return

    printBarcode(selectedBarcodeId, {
      onSuccess: (result) => {
        setPrintedData((prev) => ({
          ...prev,
          [selectedBarcodeId]: result.data,
        }))
        setPendingPrint(true)
      },
      onError: (error) => {
        const apiErrors = getAxiosErrorData<IApiResponse<null>>(error)?.errors
        if (apiErrors?.[0]?.message) {
          apiErrors.forEach((apiError) =>
            toaster.error({ title: apiError.message }),
          )
          return
        }
        toaster.error({
          title: t(
            'laboratory_research.blood_collection.barcode_modal.print_error',
          ),
        })
      },
    })
  }

  const genderLabel = useMemo(() => {
    if (!printData?.gender) return ''
    const normalized = printData.gender.toUpperCase().trim()
    if (normalized === 'MALE' || printData.gender === 'Мужской')
      return t('laboratory_research.blood_collection.gender.MALE')
    if (normalized === 'FEMALE' || printData.gender === 'Женский')
      return t('laboratory_research.blood_collection.gender.FEMALE')
    return printData.gender
  }, [printData, t])

  const analysisNameKey =
    i18n.language === 'uz' ? 'analysis_name_en' : 'analysis_name_ru'

  const isLoading = !printData

  return (
    <Dialog.Root
      open={open}
      onOpenChange={({ open: isOpen }) => {
        if (!isOpen) onClose()
      }}
      size="xl"
      scrollBehavior="inside"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="bg.surface" maxW="960px">
            <Dialog.CloseTrigger
              asChild
              position="absolute"
              top={4}
              right={4}
              zIndex={1}
            >
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>

            <Dialog.Body p={{ base: 5, md: 7 }}>
              {barcodes.length > 1 && (
                <HStack gap={2} mb={5} flexWrap="wrap">
                  {barcodes.map((barcode, index) => (
                    <Button
                      key={barcode.id}
                      size="xs"
                      borderRadius="full"
                      variant={
                        barcode.id === selectedBarcodeId ? 'solid' : 'outline'
                      }
                      bg={
                        barcode.id === selectedBarcodeId
                          ? 'primary.fg'
                          : undefined
                      }
                      color={
                        barcode.id === selectedBarcodeId ? 'white' : undefined
                      }
                      onClick={() => setSelectedBarcodeId(barcode.id)}
                    >
                      {t(
                        'laboratory_research.blood_collection.barcode_modal.tube_label',
                        { number: index + 1 },
                      )}
                    </Button>
                  ))}
                </HStack>
              )}

              <Grid
                templateColumns={{ base: '1fr', md: '360px 1fr' }}
                gap={{ base: 6, md: 8 }}
                alignItems="flex-start"
              >
                {/* ── Left: label preview ──────────────────────── */}
                <GridItem>
                  <Text
                    fontSize="xs"
                    fontWeight="bold"
                    color="fg.muted"
                    letterSpacing="0.08em"
                    textTransform="uppercase"
                  >
                    {t(
                      'laboratory_research.blood_collection.barcode_modal.preview_title',
                    )}
                  </Text>
                  <Text fontSize="sm" color="fg.muted" mt={1} mb={4}>
                    {t(
                      'laboratory_research.blood_collection.barcode_modal.preview_subtitle',
                    )}
                  </Text>

                  <Box mb={4}>
                    <LabelPrintSettings settings={printSettings} />
                  </Box>

                  {/* The label renders at true physical size; the wrapper only
                      magnifies it on screen, so the print stays 1:1. */}
                  <Box
                    w={`calc(${paper.widthMm}mm * ${previewScale})`}
                    h={`calc(${paper.heightMm}mm * ${previewScale})`}
                    maxW="full"
                    borderWidth="1px"
                    borderColor="#d4d4d4"
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    {isLoading || !selectedBarcode ? (
                      <Skeleton h="full" w="full" />
                    ) : (
                      <Box
                        transform={`scale(${previewScale})`}
                        transformOrigin="top left"
                      >
                        <LabelSheet
                          ref={previewRef}
                          paper={paper}
                          position={position}
                        >
                          <BarcodeLabelPreview
                            printData={printData}
                            barcode={selectedBarcode}
                            geometry={geometry}
                          />
                        </LabelSheet>
                      </Box>
                    )}
                  </Box>
                </GridItem>

                {/* ── Right: configuration ─────────────────────── */}
                <GridItem minW={0}>
                  <AppCard.Root>
                    <AppCard.Body p={6}>
                      <Text fontSize="xl" fontWeight="bold" mb={6}>
                        {t(
                          'laboratory_research.blood_collection.barcode_modal.config_title',
                        )}
                      </Text>

                      {isLoading ? (
                        <Skeleton h="200px" borderRadius="md" />
                      ) : (
                        <>
                          <Grid templateColumns="1fr 1fr" gapY={6} gapX={4}>
                            <ConfigField
                              label={t(
                                'laboratory_research.blood_collection.barcode_modal.donor_data',
                              )}
                              value={printData.donor_full_name}
                              hint={`${t('laboratory_research.blood_collection.barcode_modal.birth_date')}: ${formatBirthDate(
                                printData.birth_date,
                              )} · ${t('laboratory_research.blood_collection.barcode_modal.gender')}: ${genderLabel}`}
                            />
                            <div></div>
                            {/* <ConfigField
                              label={t(
                                'laboratory_research.blood_collection.barcode_modal.order_number',
                              )}
                              value={formatOrderNumber(
                                printData.donor_card_number,
                                selectedBarcode?.tube_number,
                              )}
                            /> */}
                            <ConfigField
                              label={t(
                                'laboratory_research.blood_collection.barcode_modal.tube_type',
                              )}
                              value={
                                selectedBarcode?.tube_type_name
                                  ? `${selectedBarcode.tube_type_name} (${printData.tube_color_name})`
                                  : printData.tube_color_name
                              }
                            />
                            <ConfigField
                              label={t(
                                'laboratory_research.blood_collection.barcode_modal.collection_date',
                              )}
                              value={formatConfigDateTime(
                                printData.collection_datetime,
                                i18n.language,
                              )}
                            />
                          </Grid>

                          <Text fontWeight="bold" mt={7} mb={3} fontSize="md">
                            {t(
                              'laboratory_research.blood_collection.barcode_modal.analyses',
                            )}
                          </Text>
                          <Wrap gap={2}>
                            {printData.analyses.map((analysis) => (
                              <Badge
                                key={analysis.analysis_code}
                                px={3}
                                py={1.5}
                                borderRadius="md"
                                fontSize="sm"
                                fontWeight="medium"
                                bg="badge.green.20"
                                color="badge.green.100"
                              >
                                {analysis[analysisNameKey] ||
                                  analysis.analysis_code}
                              </Badge>
                            ))}
                          </Wrap>
                        </>
                      )}
                    </AppCard.Body>
                  </AppCard.Root>

                  <Flex justify="flex-end" mt={5}>
                    <Button
                      bg="primary.solid"
                      color="white"
                      borderRadius="lg"
                      size="lg"
                      px={8}
                      gap={2}
                      disabled={isLoading || isPrinting}
                      loading={isPrinting}
                      onClick={handlePrint}
                      _hover={{ opacity: 0.9 }}
                      _active={{ opacity: 0.8 }}
                    >
                      <Printer size={18} />
                      {t(
                        'laboratory_research.blood_collection.barcode_modal.print',
                      )}
                    </Button>
                  </Flex>
                </GridItem>
              </Grid>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

function ConfigField({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <Box>
      <Text fontSize="sm" color="fg.muted" mb={1}>
        {label}
      </Text>
      <Text fontSize="lg" fontWeight="bold" lineHeight={1.2}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color="fg.muted" mt={1}>
          {hint}
        </Text>
      )}
    </Box>
  )
}
